import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { DialogModule } from 'primeng/dialog';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { JalaliDateInputComponent } from '../../shared/design-system/jalali-date-input.component';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CommissionClawbackRecord,
  CommissionPayoutRecord,
  CommissionPlanRecord,
  CommissionRuleRecord,
  CreateCommissionPlanPayload,
  SalesApiService
} from './sales-api.service';
import { SalesAiCopilotPanelComponent } from './sales-ai-copilot-panel.component';

@Component({
  standalone: true,
  selector: 'ain-sales-commission-plan-detail',
  imports: [
    AinAmountPipe, AinDatePipe, AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule,
    InputTextModule, InputNumberModule, SelectModule, TableModule, DialogModule, CurrencySelectorComponent,
    JalaliDateInputComponent, SalesAiCopilotPanelComponent
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('sales.commissions.form.createTitle') : t('sales.commissions.form.editTitle')" [description]="t('sales.commissions.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <header class="card">
          <div class="toolbar">
            <a class="back-link" [routerLink]="['/sales/commission-plans']">{{ t('catalog.action.backToList') }}</a>
            @if (isCreateMode()) {
              <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
            } @else {
              <button pButton type="button" [label]="t('sales.commissions.calculate')" [disabled]="calculating()" (click)="openCalculate()"></button>
            }
          </div>
        </header>
        @if (isCreateMode()) {
          <section class="card grid">
            <label><span>{{ t('sales.commissions.fields.planCode') }}</span><input pInputText [(ngModel)]="form.planCode" /></label>
            <label><span>{{ t('sales.commissions.fields.name') }}</span><input pInputText [(ngModel)]="form.name" /></label>
            <label><span>{{ t('sales.commissions.fields.currency') }}</span><ain-currency-selector [(ngModel)]="form.currencyCode" /></label>
            <label><span>{{ t('sales.commissions.fields.effectiveFrom') }}</span><ain-date-input [(ngModel)]="form.effectiveFromUtc" /></label>
            <label><span>{{ t('sales.commissions.fields.effectiveUntil') }}</span><ain-date-input [(ngModel)]="form.effectiveUntilUtc" /></label>
            <label><span>{{ t('sales.commissions.fields.targetAmount') }}</span><p-inputNumber [(ngModel)]="form.targetAmount" [min]="0" /></label>
            <label class="full"><span>{{ t('sales.commissions.fields.ratePercent') }}</span><p-inputNumber [(ngModel)]="form.ratePercent" [min]="0" [max]="100" suffix="%" /></label>
          </section>
        } @else if (plan()) {
          <section class="card summary">
            <article><span>{{ t('sales.commissions.fields.planCode') }}</span><strong>{{ plan()!.planCode }}</strong></article>
            <article><span>{{ t('sales.commissions.fields.name') }}</span><strong>{{ plan()!.name }}</strong></article>
            <article><span>{{ t('sales.commissions.fields.currency') }}</span><strong>{{ plan()!.currencyCode }}</strong></article>
            <article><span>{{ t('sales.commissions.fields.effectiveFrom') }}</span><strong>{{ plan()!.effectiveFromUtc | ainDate }}</strong></article>
            <article><span>{{ t('columns.status') }}</span><strong>{{ statusLabel(plan()!.status) }}</strong></article>
            @if (plan()!.targetAmount) {
              <article><span>{{ t('sales.commissions.fields.targetAmount') }}</span><strong>{{ plan()!.targetAmount | ainAmount }} {{ plan()!.currencyCode }}</strong></article>
            }
          </section>
          <div class="tabs">
            <button type="button" [class.active]="activeTab()==='rules'" (click)="setTab('rules')">{{ t('sales.commissions.tabs.rules') }}</button>
            <button type="button" [class.active]="activeTab()==='payouts'" (click)="setTab('payouts')">{{ t('sales.commissions.tabs.payouts') }}</button>
            <button type="button" [class.active]="activeTab()==='clawbacks'" (click)="setTab('clawbacks')">{{ t('sales.commissions.tabs.clawbacks') }}</button>
          </div>
          @if (activeTab()==='rules') {
            <p-table [value]="rules()" [paginator]="true" [rows]="12">
              <ng-template pTemplate="header">
                <tr>
                  <th>#</th>
                  <th>{{ t('sales.commissions.fields.ruleName') }}</th>
                  <th>{{ t('sales.commissions.fields.ruleType') }}</th>
                  <th>{{ t('sales.commissions.fields.ratePercent') }}</th>
                  <th>{{ t('sales.commissions.fields.fixedAmount') }}</th>
                  <th>{{ t('sales.commissions.fields.minBasis') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.sequence }}</td>
                  <td>{{ row.name }}</td>
                  <td>{{ ruleTypeLabel(row.ruleType) }}</td>
                  <td>{{ row.ruleType === 'PercentOfBasis' || row.ruleType === 'AcceleratorPercentOfBasis' ? row.ratePercent + '%' : '-' }}</td>
                  <td>{{ row.ruleType === 'FixedPerCalculation' ? (row.fixedAmount | ainAmount) : '-' }}</td>
                  <td>{{ row.minBasisAmount | ainAmount }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
          @if (activeTab()==='payouts') {
            <p-table [value]="payouts()" [paginator]="true" [rows]="12">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('sales.commissions.fields.calculatedAt') }}</th>
                  <th>{{ t('sales.commissions.fields.basisAmount') }}</th>
                  <th>{{ t('sales.commissions.fields.commissionAmount') }}</th>
                  <th>{{ t('sales.commissions.fields.netAmount') }}</th>
                  <th>{{ t('columns.status') }}</th>
                  <th></th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.calculatedAtUtc | ainDate:'datetime' }}</td>
                  <td>{{ row.basisAmount | ainAmount }} {{ row.currencyCode }}</td>
                  <td>{{ row.commissionAmount | ainAmount }} {{ row.currencyCode }}</td>
                  <td>{{ row.netAmount | ainAmount }} {{ row.currencyCode }}</td>
                  <td>{{ payoutStatusLabel(row.status) }}</td>
                  <td>
                    @if (row.netAmount > 0) {
                      <button pButton type="button" size="small" [label]="t('sales.commissions.clawback')" (click)="openClawback(row)"></button>
                    }
                  </td>
                </tr>
              </ng-template>
            </p-table>
          }
          @if (activeTab()==='clawbacks') {
            <p-table [value]="clawbacks()" [paginator]="true" [rows]="12">
              <ng-template pTemplate="header">
                <tr>
                  <th>{{ t('sales.commissions.fields.clawbackAt') }}</th>
                  <th>{{ t('sales.commissions.fields.amount') }}</th>
                  <th>{{ t('sales.commissions.fields.reason') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td>{{ row.clawbackAtUtc | ainDate:'datetime' }}</td>
                  <td>{{ row.amount | ainAmount }} {{ plan()?.currencyCode }}</td>
                  <td>{{ row.reason }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        }
        @if (!isCreateMode() && plan()) {
          <ain-sales-ai-copilot-panel entityType="CommissionPlan" [entityId]="plan()!.id" defaultRecommendationType="NextBestAction" />
        }
        @if (errorMessage()) { <p class="error">{{ errorMessage() }}</p> }
      }
    </ain-page>
    <p-dialog [header]="t('sales.commissions.calculate')" [visible]="showCalculateDialog()" (visibleChange)="showCalculateDialog.set($event)" [modal]="true" [style]="{width:'400px'}">
      <label><span>{{ t('sales.commissions.fields.basisAmount') }}</span><p-inputNumber [(ngModel)]="calcForm.basisAmount" [min]="0" /></label>
      <label><span>{{ t('sales.commissions.fields.idempotencyKey') }}</span><input pInputText [(ngModel)]="calcForm.idempotencyKey" /></label>
      <p class="hint">{{ t('sales.commissions.calculateHint') }}</p>
      @if (lastCalcAmount() !== null) {
        <p class="preview"><strong>{{ t('sales.commissions.fields.commissionAmount') }}:</strong> {{ lastCalcAmount() | ainAmount }} {{ plan()?.currencyCode }}</p>
      }
      <ng-template pTemplate="footer">
        <button pButton type="button" [label]="t('sales.commissions.calculate')" [disabled]="calculating()" (click)="submitCalculate()"></button>
      </ng-template>
    </p-dialog>
    <p-dialog [header]="t('sales.commissions.clawback')" [visible]="showClawbackDialog()" (visibleChange)="showClawbackDialog.set($event)" [modal]="true" [style]="{width:'360px'}">
      <label><span>{{ t('sales.commissions.fields.amount') }}</span><p-inputNumber [(ngModel)]="clawbackForm.amount" [min]="0" /></label>
      <label><span>{{ t('sales.commissions.fields.reason') }}</span><input pInputText [(ngModel)]="clawbackForm.reason" /></label>
      <ng-template pTemplate="footer">
        <button pButton type="button" [label]="t('common.save')" [disabled]="clawbacking()" (click)="submitClawback()"></button>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .card{border:1px solid #e5e7eb;border-radius:12px;padding:12px;margin-bottom:10px;background:#fff}
    .toolbar{display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:8px}
    .back-link{color:#1d4ed8;font-weight:600;text-decoration:none}
    .grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px}
    label{display:grid;gap:6px}
    label.full{grid-column:1/-1}
    .summary{display:grid;grid-template-columns:repeat(auto-fill,minmax(220px,1fr));gap:12px;font-size:14px}
    .summary article{display:grid;gap:4px}
    .summary span{color:#6b7280;font-size:12px}
    .tabs{display:flex;gap:8px;margin-bottom:10px}
    .tabs button{border:1px solid #cbd5e1;background:#fff;border-radius:999px;padding:6px 12px;cursor:pointer}
    .tabs button.active{background:#1d4ed8;color:#fff;border-color:#1d4ed8}
    .error,.hint{color:#6b7280;font-size:13px}
    .error{color:#b91c1c}
    .preview{margin-top:8px}
    @media (max-width:760px){.grid{grid-template-columns:1fr}}
  `]
})
export class SalesCommissionPlanDetailComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly calculating = signal(false);
  readonly clawbacking = signal(false);
  readonly isCreateMode = signal(false);
  readonly errorMessage = signal('');
  readonly activeTab = signal<'rules' | 'payouts' | 'clawbacks'>('rules');
  readonly plan = signal<CommissionPlanRecord | null>(null);
  readonly rules = signal<CommissionRuleRecord[]>([]);
  readonly payouts = signal<CommissionPayoutRecord[]>([]);
  readonly clawbacks = signal<CommissionClawbackRecord[]>([]);
  readonly showCalculateDialog = signal(false);
  readonly showClawbackDialog = signal(false);
  readonly lastCalcAmount = signal<number | null>(null);

  clawbackTarget: CommissionPayoutRecord | null = null;
  calcForm = { basisAmount: 0, idempotencyKey: '' };
  clawbackForm = { amount: 0, reason: 'adjustment' };
  form = {
    planCode: '',
    name: '',
    currencyCode: 'IRR',
    effectiveFromUtc: null as string | null,
    effectiveUntilUtc: null as string | null,
    targetAmount: 0,
    ratePercent: 10
  };

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('planId');
    this.isCreateMode.set(!id || id === 'new');
    if (this.isCreateMode()) {
      this.loading.set(false);
      return;
    }
    this.loadPlan(id!);
  }

  setTab(tab: 'rules' | 'payouts' | 'clawbacks'): void {
    this.activeTab.set(tab);
    const id = this.route.snapshot.paramMap.get('planId');
    if (!id) return;
    if (tab === 'payouts') this.loadPayouts(id);
    if (tab === 'clawbacks') this.loadClawbacks(id);
  }

  loadPlan(id: string): void {
    this.loading.set(true);
    this.api.getCommissionPlan(id).subscribe({
      next: p => this.plan.set(p),
      error: () => this.errorMessage.set(this.t('sales.commissions.errorLoad'))
    });
    this.api.listCommissionRules(id).subscribe({
      next: rows => { this.rules.set(rows); this.loading.set(false); },
      error: () => { this.errorMessage.set(this.t('sales.commissions.errorLoad')); this.loading.set(false); }
    });
    this.loadPayouts(id);
    this.loadClawbacks(id);
  }

  loadPayouts(id: string): void {
    this.api.listCommissionPayouts(id).subscribe({
      next: rows => this.payouts.set(rows),
      error: () => this.payouts.set([])
    });
  }

  loadClawbacks(id: string): void {
    this.api.listCommissionClawbacks(id).subscribe({
      next: rows => this.clawbacks.set(rows),
      error: () => this.clawbacks.set([])
    });
  }

  save(): void {
    if (!this.form.planCode.trim() || !this.form.name.trim()) {
      this.errorMessage.set(this.t('sales.commissions.errorSave'));
      return;
    }
    this.saving.set(true);
    const payload: CreateCommissionPlanPayload = {
      planCode: this.form.planCode,
      name: this.form.name,
      currencyCode: this.form.currencyCode,
      effectiveFromUtc: this.form.effectiveFromUtc ?? new Date().toISOString(),
      effectiveUntilUtc: this.form.effectiveUntilUtc,
      targetAmount: this.form.targetAmount > 0 ? this.form.targetAmount : null,
      notes: null,
      rules: [{
        sequence: 1,
        name: 'Default rate',
        ruleType: 'PercentOfBasis',
        ratePercent: this.form.ratePercent,
        fixedAmount: 0,
        minBasisAmount: 0,
        maxCommissionAmount: null,
        salesChannelId: null,
        isActive: true
      }]
    };
    this.api.createCommissionPlan(payload).subscribe({
      next: plan => void this.router.navigate(['/sales/commission-plans', plan.id]),
      error: () => { this.errorMessage.set(this.t('sales.commissions.errorSave')); this.saving.set(false); }
    });
  }

  openCalculate(): void {
    this.calcForm.idempotencyKey = `calc-${Date.now()}`;
    this.lastCalcAmount.set(null);
    this.showCalculateDialog.set(true);
  }

  submitCalculate(): void {
    const planId = this.route.snapshot.paramMap.get('planId')!;
    if (!this.calcForm.idempotencyKey.trim()) return;
    this.calculating.set(true);
    this.api.calculateCommission({
      commissionPlanId: planId,
      salesOrderId: null,
      basisAmount: this.calcForm.basisAmount,
      payeeUserId: null,
      idempotencyKey: this.calcForm.idempotencyKey,
      persist: true
    }).subscribe({
      next: result => {
        this.lastCalcAmount.set(result.commissionAmount);
        this.calculating.set(false);
        this.showCalculateDialog.set(false);
        this.loadPayouts(planId);
        if (this.activeTab() === 'payouts') this.setTab('payouts');
      },
      error: () => { this.errorMessage.set(this.t('sales.commissions.calculateError')); this.calculating.set(false); }
    });
  }

  openClawback(row: CommissionPayoutRecord): void {
    this.clawbackTarget = row;
    this.clawbackForm.amount = row.netAmount;
    this.clawbackForm.reason = 'adjustment';
    this.showClawbackDialog.set(true);
  }

  submitClawback(): void {
    if (!this.clawbackTarget) return;
    const planId = this.route.snapshot.paramMap.get('planId')!;
    this.clawbacking.set(true);
    this.api.recordCommissionClawback({
      commissionPayoutId: this.clawbackTarget.id,
      amount: this.clawbackForm.amount,
      reason: this.clawbackForm.reason,
      idempotencyKey: `claw-${Date.now()}`
    }).subscribe({
      next: () => {
        this.showClawbackDialog.set(false);
        this.clawbacking.set(false);
        this.loadPayouts(planId);
        this.loadClawbacks(planId);
      },
      error: () => { this.errorMessage.set(this.t('sales.commissions.clawbackError')); this.clawbacking.set(false); }
    });
  }

  statusLabel(status: string): string {
    const key = `sales.commissions.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  payoutStatusLabel(status: string): string {
    const key = `sales.commissions.payoutStatus.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  ruleTypeLabel(ruleType: string): string {
    const key = `sales.commissions.ruleTypes.${ruleType.charAt(0).toLowerCase()}${ruleType.slice(1)}`;
    const translated = this.t(key);
    return translated === key ? ruleType : translated;
  }

  t(key: string): string { return this.localization.translate(key); }
}
